import {Component, inject, Input} from '@angular/core';
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {FormatCurrencyPipe} from "../../pipes/format-currency.pipe";
import {AsyncPipe, DatePipe} from "@angular/common";
import {
  WinnerPayoutOptionsComponent
} from "../../components/winner-payouts/winner-payout-options/winner-payout-options.component";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {AgGridAngular} from "ag-grid-angular";
import {LoadingCardContentComponent} from "../../shared/loading-card-content/loading-card-content.component";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {IWinnerPayoutWithWithWinnerInfo} from "../../interfaces/IWinnerPayoutWithWithWinnerInfo";
import {WinnerPayoutStatusPipe} from "../../pipes/winner-payout-status.pipe";
import {WinnerPayoutService} from "../../services/winner-payout.service";
import {Observable} from "rxjs";
import {ContainerComponent} from "@coreui/angular";
import {ChooseYourGameComponent} from "../../components/choose-your-game/choose-your-game.component";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {PermissionsService} from "../../services/permissions.service";
import {PermissionTypes} from "../../enum/PermissionTypes";

@Component({
  selector: 'app-winner-payouts',
  standalone: true,
  templateUrl: './winner-payouts.component.html',
  imports: [
    AgGridAngular,
    LoadingCardContentComponent,
    AsyncPipe,
    ContainerComponent,
    ChooseYourGameComponent
  ],
  styleUrls: ['./winner-payouts.component.css']
})
export class WinnerPayoutsComponent {

  private currencyPipe: FormatCurrencyPipe = inject(FormatCurrencyPipe);
  private winnerPayoutService: WinnerPayoutService = inject(WinnerPayoutService);
  private winnerPayoutStatusPipe: WinnerPayoutStatusPipe = inject(WinnerPayoutStatusPipe);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private datePipe: DatePipe = inject(DatePipe);
  private permissionsService: PermissionsService = inject(PermissionsService);


  @Input() set gameSetter(gameData: { gameP: IGameQueryResult, isCrossGame: boolean }) {
    this.activeGame = gameData.gameP;
    this.fetchWinnerPayoutsPerGame();
    this.winnerPayoutDefs = this.generateColDefs(gameData.isCrossGame);
  }

  public winnerPayouts$: Observable<IWinnerPayoutWithWithWinnerInfo[]> = this.winnerPayoutService.selectPayouts$();
  public winnersGridApi!: GridApi<IWinnerPayoutWithWithWinnerInfo>;
  public activeGame: IGameQueryResult | null = null;
  uiState = UIStateEnum.ShowLoading;
  protected readonly UIStateEnum = UIStateEnum;
  protected winnerPayoutDefs: ColDef[] = [];

  public generateColDefs(isCrossGame: boolean): ColDef[] {
    const baseCols: ColDef[] = [
      {
        headerName: 'Draw Date',
        field: 'DrawTime',
        resizable: true,
        valueFormatter: params => {
          const convertedDate = this.datePipe.transform(params.value, 'MMM d, y');
          return convertedDate ? convertedDate : '';
        },
        sortable: true
      },
      {
        headerName: 'Winner Name',
        resizable: true,
        valueGetter: params => {
          let payout = params.data as IWinnerPayoutWithWithWinnerInfo;
          return payout.PlayerFirstName + ' ' + payout.PlayerLastName;
        },
        sortable: true
      },
      {
        headerName: 'Player Winnings',
        resizable: true,
        wrapHeaderText: true,
        sortable: true,
        field: 'PlayerWinnings',
        valueFormatter: params => {
          return this.currencyPipe.transform(params.value);
        }
      },
      {
        headerName: 'Payout Status',
        resizable: true,
        wrapHeaderText: true,
        sortable: true,
        field: 'PayoutStatus',
        valueFormatter: params => {
          return this.winnerPayoutStatusPipe.transform(params.value);
        }
      },
      {
        headerName: 'Card Last 4',
        resizable: true,
        wrapHeaderText: true,
        sortable: true,
        field: 'CardLastFour',
        valueFormatter: params => {
          return params.value ? `****${params.value}` : 'N/A';
        }
      }
    ];

    if (this.permissionsService.userHasPermission(PermissionTypes.ModifyWinnerPayouts)) {
      baseCols.push({
        headerName: 'Actions',
        resizable: true,
        cellRenderer: WinnerPayoutOptionsComponent,
        cellRendererParams: {
          gameId: this.activeGame?.Id,
          isCrossGame: isCrossGame
        }
      });
    }

    return baseCols;
  }

  fetchWinnerPayoutsPerGame(): void {
    this.winnerPayoutService.fetchWinnerPayouts(this.activeGame?.Id!).subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    })
  }


  onWinnerPayoutGridReady(params: GridReadyEvent<IWinnerPayoutWithWithWinnerInfo>) {
    this.winnersGridApi = params.api;
    this.winnersGridApi.sizeColumnsToFit({
      defaultMinWidth: 90,
    });
  }
}
